/**
 * MobileStrategyCreate Component
 *
 * Mobile page for creating a new strategy.
 * Route: /settings/strategies/new
 */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Input,
  TextArea,
  Alert,
  useDisclosure,
  useToastNotification,
  Breadcrumbs,
  BreadcrumbItem,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
} from '@enfomentis/ui';
import { strategyService } from '@/services/StrategyService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { useStrategiesPlaybooks } from '../context';
import { UnsavedChangesDialog } from '../components';

export const MobileStrategyCreate: React.FC = () => {
  const navigate = useNavigate();
  const unsavedChangesDialog = useDisclosure();
  const { showToast } = useToastNotification();
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Context actions for post-creation state management
  const { setShowArchived, selectStrategy, addStrategyToList } = useStrategiesPlaybooks();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [philosophy, setPhilosophy] = useState('');

  // Validation and submission state
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dirty state - any field has content
  const isDirty = name.trim() !== '' || description.trim() !== '' || philosophy.trim() !== '';

  // Pending navigation target when showing unsaved changes dialog
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Handle back navigation with dirty check
  const handleBack = useCallback(() => {
    if (isDirty) {
      setPendingNavigation('/settings/strategies');
      unsavedChangesDialog.onOpen();
    } else {
      navigate('/settings/strategies');
    }
  }, [isDirty, navigate, unsavedChangesDialog]);

  // Handle navigation to Settings with dirty check
  const handleNavigateToSettings = useCallback(() => {
    if (isDirty) {
      setPendingNavigation('/settings');
      unsavedChangesDialog.onOpen();
    } else {
      navigate('/settings');
    }
  }, [isDirty, navigate, unsavedChangesDialog]);

  // Handle discard from dialog
  const handleDiscard = useCallback(() => {
    unsavedChangesDialog.onClose();
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [unsavedChangesDialog, navigate, pendingNavigation]);

  // Handle cancel button
  const handleCancel = useCallback(() => {
    if (isDirty) {
      unsavedChangesDialog.onOpen();
    } else {
      navigate('/settings/strategies');
    }
  }, [isDirty, navigate, unsavedChangesDialog]);

  // Handle name change - clear error when editing
  const handleNameChange = useCallback((value: string) => {
    setName(value);
    if (nameError) {
      setNameError(null);
    }
  }, [nameError]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      nameInputRef.current?.focus();
      return false;
    }
    setNameError(null);
    return true;
  }, [name]);

  // Handle form submission
  const handleCreate = useCallback(async () => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedName = name.trim();
      const trimmedDescription = description.trim();
      const trimmedPhilosophy = philosophy.trim();

      // Build create payload
      const createData: Parameters<typeof strategyService.add>[0] = {
        name: trimmedName,
        playbookIds: [],
      };

      if (trimmedDescription) {
        createData.description = trimmedDescription;
      }

      if (trimmedPhilosophy) {
        createData.philosophy = trimmedPhilosophy;
      }

      const newStrategy = await strategyService.add(createData);
      syncCoordinatorService.notifyChange('strategies', newStrategy.id);

      // Optimistically add to context list (instant UI update)
      addStrategyToList(newStrategy);

      // Ensure new strategy is visible in list (it's active, so hide archived)
      setShowArchived(false);

      // Prime context selection before navigation
      selectStrategy(newStrategy.id);

      showToast({
        title: 'Strategy Created',
        description: `"${trimmedName}" has been created`,
        color: 'success',
      });

      navigate(`/settings/strategies/${newStrategy.id}`);
    } catch (error) {
      console.error('Failed to create strategy:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create strategy';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    name,
    description,
    philosophy,
    validateForm,
    addStrategyToList,
    setShowArchived,
    selectStrategy,
    showToast,
    navigate,
  ]);

  return (
    <Card radius="none" shadow="none" className="h-full flex flex-col">
      {/* Header - Breadcrumb Navigation + Page Title */}
      <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
        <div className="flex items-center">
          <Breadcrumbs variant="light" size="sm">
            <BreadcrumbItem
              onPress={handleNavigateToSettings}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Settings
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleBack}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Strategies
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>
        <h1 className="text-lg font-semibold text-text-primary mt-1">New Strategy</h1>
      </CardHeader>

      {/* Content */}
      <CardBody className="flex-1 px-1 pb-2">
        <Card>
          <CardBody className="space-y-4">
            {/* Name (required) */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Name <span className="text-danger">*</span>
              </label>
              <Input
                ref={nameInputRef}
                placeholder="Enter strategy name"
                value={name}
                onValueChange={handleNameChange}
                isInvalid={!!nameError}
                errorMessage={nameError}
                autoFocus
                className="[&_input]:text-base"
              />
            </div>

            {/* Description (optional) */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Description
              </label>
              <TextArea
                placeholder="Brief description of this strategy..."
                value={description}
                onValueChange={setDescription}
                minRows={2}
                maxRows={4}
                className="[&_textarea]:text-base"
              />
            </div>

            {/* Philosophy (optional) */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Philosophy
              </label>
              <TextArea
                placeholder="Describe the trading philosophy for this strategy..."
                value={philosophy}
                onValueChange={setPhilosophy}
                minRows={3}
                maxRows={6}
                className="[&_textarea]:text-base"
              />
            </div>

            {/* Error Alert */}
            {submitError && (
              <Alert color="danger" variant="flat">
                {submitError}
              </Alert>
            )}
          </CardBody>
        </Card>
      </CardBody>

      {/* Footer */}
      <CardFooter className="flex items-center justify-end gap-1 pt-2 border-t border-default-200">
        <Button
          variant="flat"
          size="sm"
          onPress={handleCancel}
          isDisabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          size="sm"
          onPress={handleCreate}
          isLoading={isSubmitting}
        >
          Create
        </Button>
      </CardFooter>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={unsavedChangesDialog.isOpen}
        onClose={unsavedChangesDialog.onClose}
        onDiscard={handleDiscard}
      />
    </Card>
  );
};
